import axios from 'axios';
import { isEmpty } from 'lodash';
import * as Sentry from '@sentry/react';
import { getAuthService } from './authService';
import { convertAllDecimalsToFourPlacesOptimized } from '../utils/global';
import { getDecimalConfig, consumeNextRequestConfig } from '../utils/apiDecimalConfig';
import { getRoleId, getTenantById, getTenantId } from '@apps/customer/utils/tenantHelpers';

const requestArray: any = [];
const authService = getAuthService();

let authenticatedHomeTenantId = '';
const authenticatedHomeTenantRegistrations = new Map<string, string>();

const refreshAuthenticatedHomeTenantId = (): void => {
  const registered = Array.from(authenticatedHomeTenantRegistrations.values()).filter(Boolean);
  authenticatedHomeTenantId = registered.at(-1) ?? '';
};

/** Register one host surface as a tenant-identity source. */
export const registerAuthenticatedHomeTenantId = (
  owner: string,
  tenantId?: string | null
): void => {
  const key = String(owner || '').trim();
  if (!key) throw new Error('Tenant registration owner is required');
  const value = String(tenantId ?? '').trim();
  if (value) authenticatedHomeTenantRegistrations.set(key, value);
  else authenticatedHomeTenantRegistrations.delete(key);
  refreshAuthenticatedHomeTenantId();
};

export const unregisterAuthenticatedHomeTenantId = (owner: string): void => {
  authenticatedHomeTenantRegistrations.delete(String(owner || '').trim());
  refreshAuthenticatedHomeTenantId();
};

/** Backward-compatible single-owner registration. */
export const setAuthenticatedHomeTenantId = (tenantId?: string | null): void => {
  registerAuthenticatedHomeTenantId('legacy', tenantId);
};

export const resolveRequestTenantId = (): string => {
  const switchedTenantId = String(getTenantId() ?? '').trim();
  if (switchedTenantId) {
    return switchedTenantId;
  }

  // tenantHelpers may already know the current home tenant. Use it when
  // available so ordinary Axios and fetch requests do not depend solely on
  // the chatbot overlay registration timing.
  const helperTenantId = String(getTenantById()?.tenantId ?? '').trim();
  if (helperTenantId) {
    return helperTenantId;
  }

  return authenticatedHomeTenantId;
};

export const axiosInstance = axios;
export const cancelAxios = axios.CancelToken;

axios.defaults.baseURL = process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '';

let busyApiCall = false;
let busyTenantDeleted = false;
window.addEventListener('PERMISSION_HANDLED', () => {
  busyApiCall = false;
  busyTenantDeleted = false;
});
let isRetrying = false;
let refreshPromise: Promise<string | null> | null = null;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
  config: any;
}> = [];

// Process the failed queue with the new token or null if refresh failed
const processQueue = (token: string | null, error: any = null) => {
  console.log(
    `[ProcessQueue] Processing ${failedQueue.length} queued requests: ${token ? 'with new token' : 'failed - no token'
    }`
  );

  failedQueue.forEach((request, index) => {
    if (token) {
      // Retry the request with the new token
      const retryConfig = { ...request.config };
      retryConfig.__isRetryRequest = true;

      // Ensure headers exist
      if (!retryConfig.headers) {
        retryConfig.headers = {};
      }

      // Re-resolve host scope before retrying. The original request may have
      // been created before the authenticated home tenant was registered.
      const retryRoleId = getRoleId();
      const retryTenantId = resolveRequestTenantId();

      removeRequestHeader(retryConfig.headers, 'X-Switch-Source-Role-Id');
      removeRequestHeader(retryConfig.headers, 'x-switch-source-role-id');
      if (retryRoleId) {
        setRequestHeader(retryConfig.headers, 'X-Switch-Source-Role-Id', retryRoleId);
      }

      removeRequestHeader(retryConfig.headers, 'X-Tenant-Id');
      removeRequestHeader(retryConfig.headers, 'x-tenant-id');
      if (retryTenantId) {
        setRequestHeader(retryConfig.headers, 'X-Tenant-Id', retryTenantId);
      }

      // Update the Authorization headers with the new token.
      retryConfig.headers.Authorization = `Bearer ${token}`;
      retryConfig.headers.bearerToken = token || '';

      // Ensure method is preserved (important for POST/PUT requests)
      if (!retryConfig.method && request.config.method) {
        retryConfig.method = request.config.method;
      }

      // Ensure data is preserved for POST/PUT requests
      if (
        (retryConfig.method === 'post' ||
          retryConfig.method === 'put' ||
          retryConfig.method === 'patch') &&
        !retryConfig.data &&
        request.config.data
      ) {
        retryConfig.data = request.config.data;
      }

      // Preserve content-type header for requests with data
      if (retryConfig.data && request.config.headers && request.config.headers['Content-Type']) {
        retryConfig.headers['Content-Type'] = request.config.headers['Content-Type'];
      }

      console.log(
        `[ProcessQueue] Retrying request ${index + 1}/${failedQueue.length} to ${retryConfig.url
        } (${retryConfig.method || 'GET'})`
      );

      axiosInstance(retryConfig)
        .then((response) => {
          console.log(
            `[ProcessQueue] Request ${index + 1}/${failedQueue.length} succeeded after retry`
          );

          // Convert decimal values in retry response as well
          if (response.data) {
            try {
              // Get config from the original request
              const retryDecimalConfig = getDecimalConfig(retryConfig);
              const retrySkipAll = retryDecimalConfig.skipDecimalConversion;
              const retryExcludeFields = retryDecimalConfig.excludeDecimalFields || [];

              if (!retrySkipAll) {
                response.data = convertAllDecimalsToFourPlacesOptimized(response.data, {
                  maxDepth: 50,
                  inPlace: false,
                  shallow: false,
                  excludeFields: retryExcludeFields,
                });
              }
            } catch (conversionError) {
              console.error('[ProcessQueue] Failed to convert decimal values:', conversionError);
            }
          }

          request.resolve(response);
        })
        .catch((err) => {
          console.error(
            `[ProcessQueue] Request ${index + 1}/${failedQueue.length} failed after retry:`,
            err.message
          );
          request.reject(err);
        });
    } else {
      // If token refresh failed, reject all requests in queue
      console.log(
        `[ProcessQueue] Rejecting request ${index + 1}/${failedQueue.length
        } due to failed token refresh`
      );
      request.reject(error);
    }
  });

  // Reset the queue and refreshing state
  console.log('[ProcessQueue] Reset queue and state');
  failedQueue = [];
  isRetrying = false;
  refreshPromise = null;
};

/**
 * Build the same auth + tenant headers that the Axios request interceptor adds.
 * Intended for native fetch() calls that bypass Axios (e.g. streaming uploads).
 */
export const getStreamingHeaders = async (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {};

  const roleId = getRoleId();
  const tenantId = resolveRequestTenantId();
  if (roleId) headers['X-Switch-Source-Role-Id'] = roleId;
  if (tenantId) headers['X-Tenant-Id'] = tenantId;

  if (authService.isTokenExpired()) {
    const newToken = await authService.signInSilent();
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`;
      headers.bearerToken = String(newToken);
    }
  } else {
    const token = authService.getToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
      headers.bearerToken = token;
    }
  }

  return headers;
};

const isExternalChatbotApiRequest = (config?: {
  url?: string;
  baseURL?: string;
}): boolean => {
  const requestUrl = config?.url;
  if (typeof requestUrl !== 'string' || requestUrl.length === 0) {
    return false;
  }

  try {
    const parsedUrl = new URL(requestUrl, config?.baseURL || window.location.origin);
    return (
      parsedUrl.hostname.includes('execute-api.') &&
      parsedUrl.hostname.includes('amazonaws.com')
    );
  } catch {
    return false;
  }
};

const isChatbotStreamRequest = (config?: {
  url?: string;
  baseURL?: string;
}): boolean => {
  const requestUrl = config?.url;
  if (typeof requestUrl !== 'string' || requestUrl.length === 0) {
    return false;
  }

  try {
    const parsedUrl = new URL(requestUrl, config?.baseURL || window.location.origin);
    return parsedUrl.pathname.includes('/chat/stream');
  } catch {
    return false;
  }
};

const removeRequestHeader = (headers: any, name: string): void => {
  if (!headers) return;

  if (typeof headers.delete === 'function') {
    headers.delete(name);
  }

  delete headers[name];
};

const setRequestHeader = (headers: any, name: string, value: string): void => {
  if (!headers || !value) return;

  if (typeof headers.set === 'function') {
    headers.set(name, value);
    return;
  }

  headers[name] = value;
};

const normalizeChatbotStreamHeaders = (
  headers: any,
  roleId?: string,
  tenantId?: string
): void => {
  // The chatbot package may already have resolved the home-company tenant
  // through currentTenantInfo.id. Preserve that value when getTenantId()
  // is empty because there is no switched-company #cid context.
  const existingTenantId =
    (typeof headers?.get === 'function'
      ? headers.get('X-Tenant-Id') || headers.get('x-tenant-id')
      : undefined) ||
    headers?.['X-Tenant-Id'] ||
    headers?.['x-tenant-id'];

  const resolvedTenantId = tenantId || existingTenantId;

  // Clear source-role aliases and send only the canonical header.
  removeRequestHeader(headers, 'X-Switch-Source-Role-Id');
  removeRequestHeader(headers, 'x-switch-source-role-id');
  removeRequestHeader(headers, 'X-Source');
  removeRequestHeader(headers, 'x-source');
  removeRequestHeader(headers, 'SourceRoleId');
  removeRequestHeader(headers, 'sourceRoleId');

  if (roleId) {
    setRequestHeader(headers, 'X-Switch-Source-Role-Id', roleId);
  }

  // Normalize tenant casing without discarding the package-resolved value.
  removeRequestHeader(headers, 'X-Tenant-Id');
  removeRequestHeader(headers, 'x-tenant-id');

  if (resolvedTenantId) {
    setRequestHeader(headers, 'X-Tenant-Id', String(resolvedTenantId));
  }

  // The canonical SSE endpoint uses Authorization only.
  removeRequestHeader(headers, 'bearerToken');
  removeRequestHeader(headers, 'BearerToken');
};

const isPresignedS3UploadRequest = (config?: {
  method?: string;
  url?: string;
  baseURL?: string;
}): boolean => {
  const method = config?.method?.toLowerCase?.();
  if (method !== 'put') {
    return false;
  }

  const requestUrl = config?.url;
  if (typeof requestUrl !== 'string' || requestUrl.length === 0) {
    return false;
  }

  try {
    const parsedUrl = new URL(requestUrl, config?.baseURL || window.location.origin);
    const queryParamKeys = Array.from(parsedUrl.searchParams.keys()).map((key) =>
      key.toLowerCase()
    );
    const hasPresignedSignature =
      queryParamKeys.includes('x-amz-signature') ||
      queryParamKeys.includes('x-amz-credential') ||
      queryParamKeys.includes('x-amz-algorithm') ||
      queryParamKeys.includes('awsaccesskeyid') ||
      queryParamKeys.includes('signature');

    return ['http:', 'https:'].includes(parsedUrl.protocol) && hasPresignedSignature;
  } catch {
    return false;
  }
};

// Add a request interceptor
axiosInstance.interceptors.request.use(
  async function (config) {
    // Ensure headers exist and preserve existing headers
    if (!config.headers) {
      config.headers = {};
    }

    if (isPresignedS3UploadRequest(config)) {
      return config;
    }

    const isStreamRequest = isChatbotStreamRequest(config);
    const tenantId = resolveRequestTenantId();
    const roleId = getRoleId();

    // Preserve the normal Wealthlane/API Gateway request contract for every
    // request, including non-chatbot execute-api endpoints such as /ingest/*.
    if (roleId) {
      config.headers['X-Switch-Source-Role-Id'] = roleId;
    }

    if (tenantId) {
      config.headers['X-Tenant-Id'] = tenantId;
    }

    // Attach decimal conversion config to axios config
    const decimalConfig = consumeNextRequestConfig();
    if (decimalConfig) {
      (config as any).__decimalConfig = decimalConfig;
    }

    // Check if token is expired and refresh it before making the request
    if (authService.isTokenExpired()) {
      console.log('[Request Interceptor] Token is expired, attempting to refresh');
      try {
        const newToken = await authService.signInSilent();
        if (newToken) {
          console.log('[Request Interceptor] Token refreshed successfully');
          // Add the new token to the request
          config.headers.Authorization = `Bearer ${newToken}`;
          config.headers.bearerToken = newToken || '';
        } else {
          console.log('[Request Interceptor] Token refresh returned null');
        }
      } catch (error) {
        console.error('[Request Interceptor] Failed to refresh token:', error);
        // Continue with the request - the response interceptor will handle 401 errors
      }
    } else {
      // If token is valid, add it to the request
      const token = authService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        config.headers.bearerToken = token || '';
      }
    }

    // Normalize all /chat/stream requests, whether local or deployed.
    // The canonical SSE endpoint uses Authorization and does not accept
    // the legacy bearerToken header.
    if (isStreamRequest) {
      normalizeChatbotStreamHeaders(config.headers, roleId, tenantId);
    }

    return config;
  },
  async function (error: any) {
    return await Promise.reject(error);
  }
);

// Helper to detect network/CORS errors (API down scenarios)
const isNetworkError = (error: any): boolean => {
  // Network errors occur when:
  // 1. No response is received (API down, CORS blocked, network failure)
  // 2. Error code indicates network issue
  const networkErrorCodes = [
    'ERR_NETWORK',
    'ECONNABORTED',
    'ETIMEDOUT',
    'ENOTFOUND',
    'ECONNREFUSED',
  ];
  return (
    !error.response &&
    error.request &&
    (error.message === 'Network Error' ||
      error.code === 'ERR_NETWORK' ||
      networkErrorCodes.includes(error.code))
  );
};

// Helper to get error type for categorization
const getErrorType = (error: any): string => {
  if (axios.isCancel(error)) return 'CANCELLED';
  if (isNetworkError(error)) return 'NETWORK_ERROR';
  if (error.code === 'ECONNABORTED') return 'TIMEOUT';
  if (error.response) return `HTTP_${error.response.status}`;
  return 'UNKNOWN';
};

const isMaintenanceFallbackError = (error: any): boolean => {
  if (axios.isCancel(error)) return false;
  if (isNetworkError(error)) return true;
  const status = error.response?.status;
  if (!status) return true;
  if (status === 401 || status === 403) return false;
  if (status >= 500) return true;
  const body = error.response?.data;
  if (!body?.error?.message && !body?.error?.validationErrors) return true;
  return false;
};

const scrubHeaders = (headers: any): Record<string, any> | undefined => {
  if (!headers) return undefined;
  const scrubbed = { ...headers };
  const sensitiveKeys = ['authorization', 'bearertoken', 'cookie', 'set-cookie'];
  for (const key of Object.keys(scrubbed)) {
    if (sensitiveKeys.includes(key.toLowerCase())) {
      scrubbed[key] = '[REDACTED]';
    }
  }
  return scrubbed;
};

// Add a response interceptor
axiosInstance.interceptors.response.use(
  function (response) {
    if (requestArray.length !== 0) {
      for (let i = requestArray.length - 1; i >= 0; i--) {
        if (requestArray[i] && response.config.url === requestArray[i].url) {
          requestArray.splice(i, 1);
        }
      }
    }

    // export default withExcludeDecimalFields([
    //   'exchangeRate',           // Keep raw exchange rate
    //   'conversionRatio',        // Keep raw conversion ratio
    //   'items[].ratio',          // Keep raw ratio in array items
    //   'metadata.preciseValue',  // Keep raw nested value
    // ])(useGetOptionParticipantListBase);

    // Get decimal conversion config from the request
    const decimalConfig = getDecimalConfig(response.config);
    const shouldSkipAll = decimalConfig.skipDecimalConversion;
    const excludeFields = decimalConfig.excludeDecimalFields || [];

    // Skip decimal conversion for Blob responses (file downloads)
    const isBlob = response.data instanceof Blob;
    const isBlobRequest = response.config.responseType === 'blob';
    const isChatbotApiResponse = isExternalChatbotApiRequest(response.config);

    if (response.data && !shouldSkipAll && !isBlob && !isBlobRequest && !isChatbotApiResponse) {
      try {
        response.data = convertAllDecimalsToFourPlacesOptimized(response.data, {
          maxDepth: 50, // Limit depth to prevent stack overflow
          inPlace: false, // Create new object to avoid mutation
          shallow: false, // Deep conversion for nested structures
          excludeFields: excludeFields, // Fields to skip conversion (keep raw values)
        });
      } catch (error) {
        // If conversion fails, log error but return original response
        console.error('[API Interceptor] Failed to convert decimal values:', error);
      }
    }

    return response;
  },
  async function (error: any) {
    const originalRequest = error.config;
    if (isPresignedS3UploadRequest(originalRequest)) {
      return await Promise.reject(error);
    }

    const correlationId =
      error.response?.headers?.['x-error-correlation-id'] || error.response?.data?.correlationId;
    const errorType = getErrorType(error);
    const isNetworkOrCorsError = isNetworkError(error);
    const isFallback = isMaintenanceFallbackError(error);

    if (isFallback) {
      const tenant = getTenantById();
      const roleId = getRoleId();

      Sentry.captureException(error, {
        level: 'error',
        tags: {
          error_type: 'wl_maintenance_fallback',
          correlation_id: correlationId || 'N/A',
          httpStatus: error.response?.status?.toString() || 'NO_RESPONSE',
          endpoint: error.config?.url || 'unknown',
          requestMethod: error.config?.method?.toUpperCase() || 'UNKNOWN',
          isNetworkError: String(isNetworkOrCorsError),
          tenantId: tenant?.tenantId || 'N/A',
          roleId: roleId || 'N/A',
        },
        extra: {
          errorType,
          errorMessage: error.message,
          errorCode: error.code,
          requestUrl: error.config?.url,
          requestMethod: error.config?.method,
          requestBaseURL: error.config?.baseURL,
          requestHeaders: scrubHeaders(error.config?.headers),
          responseStatus: error.response?.status,
          responseData: error.response?.data
            ? JSON.stringify(error.response.data, null, 2)
            : undefined,
          responseHeaders: scrubHeaders(error.response?.headers),
          currentPath: window.location.pathname,
          timestamp: new Date().toISOString(),
          ...(isNetworkOrCorsError && {
            networkErrorDetails: {
              message: error.message,
              code: error.code,
              possibleCause:
                'API may be down, network failure, or CORS blocked due to no response headers',
              hasRequest: !!error.request,
              hasResponse: !!error.response,
            },
          }),
        },
        fingerprint: isNetworkOrCorsError
          ? ['wl-maintenance-fallback', 'network-error', error.config?.url || 'unknown-url']
          : ['wl-maintenance-fallback', error.config?.url || 'unknown-url'],
      });
    }

    if (error.response?.status === 400 || error.response?.status === 401) {
      const tenant = getTenantById();
      const roleId = getRoleId();
      const status = error.response.status;
      const errorTag = status === 400 ? 'wl_bad_request' : 'wl_unauthorized';

      Sentry.captureException(error, {
        level: 'warning',
        tags: {
          error_type: errorTag,
          correlation_id: correlationId || 'N/A',
          httpStatus: String(status),
          endpoint: error.config?.url || 'unknown',
          requestMethod: error.config?.method?.toUpperCase() || 'UNKNOWN',
          tenantId: tenant?.tenantId || 'N/A',
          roleId: roleId || 'N/A',
        },
        extra: {
          errorType,
          errorMessage: error.message,
          requestUrl: error.config?.url,
          requestMethod: error.config?.method,
          requestData: (() => {
            try {
              return error.config?.data
                ? JSON.stringify(JSON.parse(error.config.data), null, 2)
                : undefined;
            } catch {
              return error.config?.data;
            }
          })(),
          requestHeaders: scrubHeaders(error.config?.headers),
          responseStatus: status,
          responseData: error.response?.data
            ? JSON.stringify(error.response.data, null, 2)
            : undefined,
          validationErrors: error.response?.data?.error?.validationErrors || null,
          errorDetail: error.response?.data?.error?.message || null,
          currentPath: window.location.pathname,
          timestamp: new Date().toISOString(),
        },
        fingerprint: [errorTag, error.config?.url || 'unknown-url'],
      });
    }

    // Store the original request
    if (originalRequest) {
      requestArray.push(originalRequest);
    }

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      if (!originalRequest || originalRequest.__isRetryRequest) {
        return await Promise.reject(error);
      }

      originalRequest.__isRetryRequest = true;

      console.log('[Response Interceptor] 401 Unauthorized error, queueing request for retry');
      const retryRequest = new Promise((resolve, reject) => {
        // Add to queue to retry after token refresh
        failedQueue.push({
          resolve,
          reject,
          config: originalRequest,
        });
      });

      // If we're not already refreshing the token, initiate refresh
      if (!isRetrying) {
        console.log('[Response Interceptor] Starting token refresh');
        isRetrying = true;

        // Create a promise for the token refresh that can be reused
        refreshPromise = new Promise<string | null>((resolve) => {
          authService
            .signInSilent()
            .then((token) => {
              // We got a new token, resolve with it
              console.log('[Response Interceptor] Token refresh successful');
              resolve(token as string);
            })
            .catch((refreshError) => {
              // Failed to get token
              console.error('[Response Interceptor] Failed to refresh token:', refreshError);
              resolve(null);
            });
        });

        // Process the token result
        refreshPromise.then((accessToken) => {
          if (accessToken) {
            console.log('[Response Interceptor] Processing retry queue with new token');
            // Process the queue with the new token
            processQueue(accessToken);
          } else {
            console.log('[Response Interceptor] No token obtained, rejecting all queued requests');
            // If we couldn't get a new token, reject all requests
            processQueue(null, error);

            // If no active API call is in progress, dispatch SIGN_IN event
            if (!busyApiCall) {
              busyApiCall = true;
              console.log('[Response Interceptor] Dispatching SIGN_IN event');
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('PERMISSION_CHANGED', { detail: 'SIGN_IN' }));
              }, 1000);
            }
          }
        });
      } else {
        console.log('[Response Interceptor] Token refresh already in progress, request queued');
      }

      // Return the promise that will resolve/reject based on the token refresh
      return retryRequest;
    }

    if (error.response?.status === 403 && isEmpty(error?.response?.data?.error)) {
      const requestUrl = originalRequest?.url ?? '';
      const requestMethod = (originalRequest?.method ?? 'get').toLowerCase();
      const isSignalRRequest =
        requestUrl.includes('/signalr-hubs/') ||
        requestUrl.includes('bellNotification') ||
        requestUrl.includes('/negotiate');

      // Only treat GET 403s as session-level permission changes.
      // POST/PUT/DELETE 403s are action-level — the user tried an operation
      // they're not authorized for. These should surface as error toasts,
      // not trigger a page reload.
      const isMutation = requestMethod !== 'get';

      if (!busyApiCall && !isSignalRRequest && !isMutation) {
        busyApiCall = true;
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('PERMISSION_CHANGED', { detail: 'REFRESH' }));
        }, 1000);
      }
    }

    if (
      error.response?.status === 404 &&
      error.response?.headers?.['abp-tenant-resolve-error'] === 'Tenant not found!' &&
      window.location.hash.includes('cid=')
    ) {
      if (!busyTenantDeleted) {
        busyTenantDeleted = true;
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('TENANT_DELETED'));
        }, 1000);
      }
    }

    return await Promise.reject(error);
  }
);